<form [formGroup]="form" (ngSubmit)="onSubmit()" class="step-form">
  <h3 class="step-title">Business Details</h3>
  <p class="step-subtitle">We need these details to set up your Stripe payment processing account and configure your nonprofit profile.</p>
  <p class="step-helper">✓ This information is used for identity verification and won't be displayed publicly without your consent.</p>

  <!-- Legal Name + DBA -->
  <div class="form-row two-col">
    <mat-form-field appearance="outline">
      <mat-label>Legal Organization Name</mat-label>
      <input matInput formControlName="name" placeholder="e.g. Community Food Bank">
      <mat-error *ngIf="form.get('name')?.hasError('required')">Organization name is required</mat-error>
    </mat-form-field>

    <mat-form-field appearance="outline">
      <mat-label>DBA / Doing Business As (optional)</mat-label>
      <input matInput formControlName="dba" placeholder="e.g. The Food Bank">
      <mat-hint title="The name donors will see if different from your legal name">The name donors will see if different from your legal name</mat-hint>
    </mat-form-field>
  </div>

  <!-- Org Type + EIN -->
  <div class="form-row two-col">
    <mat-form-field appearance="outline">
      <mat-label>Organization Type</mat-label>
      <mat-select formControlName="orgType">
        <mat-option *ngFor="let t of orgTypes" [value]="t.value">{{ t.label }}</mat-option>
      </mat-select>
      <mat-hint *ngIf="form.get('orgType')?.value === 'non_profit'" title="Stripe offers reduced processing rates for verified 501(c)(3) nonprofits. After onboarding, apply to Stripe's Nonprofit Program to lower your transaction fees.">
        💡 Stripe offers reduced processing rates for verified 501(c)(3) nonprofits. After onboarding, apply to Stripe's Nonprofit Program to lower your transaction fees.
      </mat-hint>
      <mat-error *ngIf="form.get('orgType')?.hasError('required')">Please select an organization type</mat-error>
    </mat-form-field>

    <mat-form-field appearance="outline">
      <mat-label>EIN / Tax ID</mat-label>
      <input matInput formControlName="ein" placeholder="12-3456789">
      <mat-hint>Format: 12-3456789</mat-hint>
      <mat-error *ngIf="form.get('ein')?.hasError('required')">EIN is required</mat-error>
      <mat-error *ngIf="form.get('ein')?.hasError('pattern')">Enter a valid EIN (XX-XXXXXXX)</mat-error>
    </mat-form-field>
  </div>

  <!-- Referral Code (optional) -->
  <div class="form-row">
    <mat-form-field appearance="outline" class="full-width">
      <mat-label>Partner Referral Code (optional)</mat-label>
      <input matInput formControlName="referralCode"
             placeholder="Enter your partner's 8-character referral code"
             autocomplete="off">
      <mat-hint title="If referred by a Chareety partner, enter their code here">If referred by a Chareety partner, enter their code here</mat-hint>
      <mat-error *ngIf="form.get('referralCode')?.hasError('minlength')">Code must be at least 6 characters</mat-error>
      <mat-error *ngIf="form.get('referralCode')?.hasError('maxlength')">Code cannot exceed 10 characters</mat-error>
    </mat-form-field>
  </div>

  <!-- Phone + Website -->
  <div class="form-row two-col">
    <mat-form-field appearance="outline">
      <mat-label>Phone Number</mat-label>
      <input matInput formControlName="phone" placeholder="+1 (555) 000-0000">
      <mat-error *ngIf="form.get('phone')?.hasError('required')">Phone number is required</mat-error>
    </mat-form-field>

    <mat-form-field appearance="outline">
      <mat-label>Website (optional)</mat-label>
      <input matInput formControlName="website" placeholder="https://yourorg.org">
    </mat-form-field>
  </div>

  <!-- Mailing Address -->
  <div class="section-divider">
    <span>Mailing Address</span>
  </div>

  <div class="form-row">
    <app-address-autocomplete formControlName="addressLookup" class="full-width"></app-address-autocomplete>
  </div>

  <!-- Hidden fields for form submission — auto-populated by address lookup -->
  <input formControlName="line1" style="display:none">
  <input formControlName="line2" style="display:none">
  <input formControlName="city" style="display:none">
  <input formControlName="state" style="display:none">
  <input formControlName="postal_code" style="display:none">

  <!-- Subdomain -->
  <div class="section-divider">
    <span>Donation Page URL</span>
  </div>

  <div class="form-row">
    <mat-form-field appearance="outline" class="full-width">
      <mat-label>Subdomain</mat-label>
      <input matInput formControlName="subDomain"
             placeholder="e.g. communityfoodbank"
             autocomplete="off">
      <span matSuffix>
        <mat-spinner *ngIf="subDomainChecking" diameter="18" style="display:inline-block"></mat-spinner>
        <mat-icon *ngIf="!subDomainChecking && subDomainAvailable === true"
                  style="color:#4caf50;vertical-align:middle">check_circle</mat-icon>
        <mat-icon *ngIf="!subDomainChecking && subDomainAvailable === false"
                  style="color:#f44336;vertical-align:middle">cancel</mat-icon>
      </span>
      <mat-hint *ngIf="subDomainPreview && subDomainAvailable !== false">
        Your page: <strong>{{ subDomainPreview }}</strong>
      </mat-hint>
      <mat-error *ngIf="form.get('subDomain')?.hasError('required')">Subdomain is required</mat-error>
      <mat-error *ngIf="form.get('subDomain')?.hasError('minlength')">Minimum 3 characters</mat-error>
      <mat-error *ngIf="form.get('subDomain')?.hasError('maxlength')">Maximum 30 characters</mat-error>
      <mat-error *ngIf="form.get('subDomain')?.hasError('pattern')">
        Only lowercase letters, numbers, and hyphens. Must start and end with a letter or number.
      </mat-error>
      <mat-error *ngIf="form.get('subDomain')?.hasError('unavailable')">
        That subdomain is already taken — please choose another.
      </mat-error>
    </mat-form-field>
  </div>

  <!-- Tax Disclosure -->
  <div class="section-divider">
    <span>Tax Disclosure Statement</span>
  </div>

  <div class="form-row">
    <mat-form-field appearance="outline" class="full-width">
      <mat-label>Disclosure Text</mat-label>
      <textarea matInput formControlName="disclosure" rows="3"
        placeholder="Your contribution is tax-deductible to the extent allowed by law...">
      </textarea>
      <mat-hint>This appears on every donation receipt. Example: 'Your contribution of [amount] is tax-deductible to the extent allowed by law. Our EIN is [XX-XXXXXXX].'</mat-hint>
      <mat-error *ngIf="form.get('disclosure')?.hasError('required')">Disclosure text is required</mat-error>
    </mat-form-field>
  </div>

  <div class="form-actions">
    <button mat-raised-button color="primary" type="submit" [disabled]="loading">
      <mat-spinner *ngIf="loading" diameter="20" class="btn-spinner"></mat-spinner>
      <span>{{ loading ? 'Saving...' : 'Save & Continue' }}</span>
    </button>
  </div>
</form>
